import React, { useState } from 'react';
import type {
  EmailDomainSetup,
  IDXSetupData,
  RemiIdxSetupData,
} from '../schemas/onboarding.schema';
import { ChoiceCard } from '../components/ChoiceCard';
import { FormSection } from '../components/FormSection';
import { NextButton } from '../components/NextButton';
import { QuestionLabel } from '../components/QuestionLabel';
import { SectionHeading } from '../components/SectionHeading';
import { StageContainer } from '../components/StageContainer';
import { Stage4IDXSetup } from './Stage4IDXSetup';
import { Stage4IDXSetupWrapper } from './Stage4IDXSetupWrapper';

export type IdxPath = 'own' | 'remi';

interface Stage4IDXFlowProps {
  onSubmitOwnIdx: (data: IDXSetupData) => void;
  onSubmitRemiIdx: (remiIdx: RemiIdxSetupData) => void;
  initialData?: {
    idx_path?: IdxPath;
    idx_display_url?: string;
    email_domain?: Partial<EmailDomainSetup>;
    [key: string]: unknown;
  };
  isLoading?: boolean;
  /** The Remi IDX site was already created (IDX Site Setup done); resume at the MLS step. */
  remiIdxSiteCreated?: boolean;
  userEmail?: string;
  userName?: string;
}

/**
 * The IDX & Email stage. "Use my own IDX" is the IDX Display URL plus Remi
 * Email Domain Setup on one page. "Create Remi's IDX for me" goes through the
 * original IDX Site Setup and MLS steps; its DNS records include Remi's email
 * records, so email uses the IDX domain.
 */
export const Stage4IDXFlow: React.FC<Stage4IDXFlowProps> = ({
  onSubmitOwnIdx,
  onSubmitRemiIdx,
  initialData,
  isLoading = false,
  remiIdxSiteCreated = false,
  userEmail = '',
  userName = '',
}) => {
  // Orgs that finished this stage before the path choice used their own IDX.
  const [idxPath, setIdxPath] = useState<IdxPath | undefined>(
    remiIdxSiteCreated
      ? 'remi'
      : (initialData?.idx_path ?? (initialData?.idx_display_url ? 'own' : undefined))
  );
  const [step, setStep] = useState<'choose' | 'remi_idx'>(
    remiIdxSiteCreated ? 'remi_idx' : 'choose'
  );

  if (step === 'remi_idx') {
    return (
      <Stage4IDXSetupWrapper
        onNext={onSubmitRemiIdx}
        initialData={initialData}
        isLoading={isLoading}
        userEmail={userEmail}
        userName={userName}
      />
    );
  }

  const pathChoice = (
    <FormSection>
      <QuestionLabel>How would you like to set up your IDX?</QuestionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <ChoiceCard
          name="idx-path"
          value="own"
          checked={idxPath === 'own'}
          onSelect={() => setIdxPath('own')}
          title="Use my own IDX"
          description="Choose this if you already have an IDX site you'd like Remi to use."
        />
        <ChoiceCard
          name="idx-path"
          value="remi"
          checked={idxPath === 'remi'}
          onSelect={() => setIdxPath('remi')}
          title="Create Remi's IDX for me"
          description="Choose this if you'd like Speculo to set up an IDX site for Remi."
        />
      </div>
    </FormSection>
  );

  if (idxPath === 'own') {
    return (
      <Stage4IDXSetup
        onNext={onSubmitOwnIdx}
        initialData={initialData}
        idxPathChoice={pathChoice}
        isLoading={isLoading}
      />
    );
  }

  return (
    <StageContainer
      title="IDX & Email"
      description="Set up the IDX site Remi uses to send properties to your leads by email and SMS, and the domain Remi sends email from."
    >
      <div className="space-y-6">
        <SectionHeading title="IDX Setup" />
        {pathChoice}
        {idxPath === 'remi' && (
          <NextButton
            type="button"
            onClick={() => {
              setStep('remi_idx');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        )}
      </div>
    </StageContainer>
  );
};
